import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import cookies from "js-cookie";
import { Breadcrumb, Title } from "../../../css/commun";
import { URL_Back, URL_Dev } from "../Variables";
import { fetchPostBySlug, fetchWpCached, fetchWpPostById, resolvePostImageUrl } from "../../../api/wpClient";
import defaultImg from "../../../photos/blog/nophoto1.png";
import defaultImg2 from "../../../photos/blog/nophoto3.png";
import "./DetailBlog.css";
import SeoHead from "../../Seo/SeoHead";

const DetailBlog = (props) => {
  const { slug } = useParams();
  const { t } = props;
  const routeLanguage = props.match?.params?.lng;
  const activeLanguage = ['fr', 'en', 'ar'].includes(routeLanguage)
    ? routeLanguage
    : (cookies.get('i18next') || 'fr');
  const homePath = `/${activeLanguage}`;
  const blogBasePath = `${homePath}/blog`;
  const [details, setDetails] = useState(null);
  const [contentText, setContentText] = useState("");
  const [dateText, setDateText] = useState("");
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    document.body.dir = activeLanguage === 'ar' ? 'rtl' : 'ltr';
  }, [activeLanguage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchPostDetails = async () => {
      try {
        const post = await fetchPostBySlug(slug, { onRevalidated: setDetails });
        if (post) {
          setDetails(post);
        } else {
          console.error("No post found");
          setDetails(null);
        }
      } catch (error) {
        console.error("Error fetching post details:", error);
        setDetails(null);
      }
    };

    fetchPostDetails();
  }, [slug]);

  useEffect(() => {
    const rendered = details?.content?.rendered;

    if (!rendered) {
      setContentText("");
      setDateText("");
      return;
    }

    const rewriteLinks = async () => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rendered, "text/html");
      const anchors = Array.from(doc.querySelectorAll("a[href]"));
      const backEndAnchors = anchors.filter((anchor) => {
        const href = anchor.getAttribute("href");
        if (!href) return false;

        try {
          const parsedUrl = new URL(href, URL_Back);
          const host = parsedUrl.hostname.replace(/^www\./, "");
          return host === "back-end.oxton-digital.com";
        } catch (error) {
          return false;
        }
      });

      const idToSlug = new Map();

      await Promise.all(
        backEndAnchors.map(async (anchor) => {
          const href = anchor.getAttribute("href");
          if (!href) return;

          let parsedUrl;
          try {
            parsedUrl = new URL(href, URL_Back);
          } catch (error) {
            return;
          }

          const postId = parsedUrl.searchParams.get("p");
          if (postId) {
            if (!idToSlug.has(postId)) {
              try {
                const result = await fetchWpPostById(postId);
                if (result?.slug) {
                  idToSlug.set(postId, result.slug);
                }
              } catch (error) {
                return;
              }
            }

            const mappedSlug = idToSlug.get(postId);
            if (mappedSlug) {
              anchor.setAttribute("href", `${blogBasePath}/${mappedSlug}`);
            }
            return;
          }

          const rawPath = parsedUrl.pathname || "";
          const pathSegments = rawPath.split("/").filter(Boolean);
          const slugFromPath = pathSegments[pathSegments.length - 1] || "";
          if (slugFromPath) {
            anchor.setAttribute("href", `${blogBasePath}/${slugFromPath}`);
          }
        })
      );

      const dateDiv = doc.querySelector(".date");
      setDateText(dateDiv?.textContent || "");

      doc.querySelectorAll(".date").forEach((node) => node.remove());

      const contentDiv = doc.querySelector(".content");
      const content = contentDiv?.innerHTML || doc.body.innerHTML || rendered;
      setContentText(content);
    };

    rewriteLinks();
  }, [blogBasePath, details]);

  const getSidebarPosts = (data) => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.filter((post) => post?.slug && post.slug !== slug).slice(0, 3);
  };

  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const all = await fetchWpCached("posts", {
          onRevalidated: (data) => {
            setRecentPosts(getSidebarPosts(data));
          },
        });
        setRecentPosts(getSidebarPosts(all));
      } catch (error) {
        console.error("Error fetching recent posts:", error);
      }
    };

    fetchRecentPosts();
  }, [slug]);

  const decodeHTML = (html) => {
    if (!html || typeof html !== "string") return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.documentElement.textContent || html;
  };

  const stripHtml = (html) => {
    if (!html || typeof html !== "string") return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  };

  const toAbsoluteUrl = (value) => {
    if (!value) {
      return `${URL_Dev}/images/logo.png`;
    }

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    return `${URL_Dev}${value.startsWith("/") ? value : `/${value}`}`;
  };

  const url = resolvePostImageUrl(details, "article_img1_content") || null;
  const postTitle = details?.title?.rendered ? decodeHTML(details.title.rendered) : "Oxton Digital | Blog";
  const postDescription = (
    details?.yoast_head_json?.og_description ||
    details?.yoast_head_json?.description ||
    stripHtml(contentText)
  ).slice(0, 160);
  const postImage = toAbsoluteUrl(url || "/images/logo.png");
  const articleUrl = `${URL_Dev}${blogBasePath}/${encodeURIComponent(slug)}`;
  const blogJsonLd = details
    ? [
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Accueil",
              item: `${URL_Dev}${homePath}`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: `${URL_Dev}${blogBasePath}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: postTitle,
              item: articleUrl,
            },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: postTitle,
          description: postDescription,
          image: [postImage],
          datePublished: details.date,
          dateModified: details.modified || details.date,
          mainEntityOfPage: articleUrl,
          author: {
            "@type": "Organization",
            name: "Oxton Digital",
          },
          publisher: {
            "@type": "Organization",
            name: "Oxton Digital",
            logo: {
              "@type": "ImageObject",
              url: `${URL_Dev}/images/oxton.png`,
            },
          },
        },
      ]
    : [];

  return (
    <>
      <SeoHead
        title={`Oxton Digital | Blog : ${postTitle}`}
        description={postDescription}
        pathname={props.location.pathname}
        image={postImage}
        type="article"
        jsonLd={blogJsonLd}
      />
      <div className="container">
        <Breadcrumb>
          <p>
            <Link className="retour" to={homePath}>{t('Home_link')}</Link> / <Link className="retour" to={blogBasePath}>Blog</Link> / <span>{details?.title ? decodeHTML(details.title.rendered) : 'Loading...'}</span>
          </p>
        </Breadcrumb>
        <Title />
        <div className="detail-blog blog-detail-content">
          <div className="blog-post">
            <div className="blog-image-container">
              <img
                src={url || defaultImg}
                alt={decodeHTML(details?.title?.rendered) || "Loading..."}
                className="blog-image img-fluid"
              />
            </div>

            <h1 className="blog-title">
              {decodeHTML(details?.title?.rendered) || "Loading..."}
            </h1>
            <div className="blog-meta">
              {dateText ? (
                <span className="blog-date d-flex gap-2">
                  {dateText}
                </span>
              ) : null}
            </div>
            <div className="blog-content title-p" dangerouslySetInnerHTML={{ __html: contentText }} />
          </div>

          <aside className="recent-posts">
            <h3 className="recent-title">Derniers articles</h3>
            <ul>
              {recentPosts.map((post) => {
                const html = post?.content?.rendered || "";
                const recentSlug = post?.slug;
                const title = post?.title?.rendered;
                const imgSrc = resolvePostImageUrl(post, "article_img3_content")?.trim();

                if (!recentSlug || !title) {
                  return null;
                }

                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");
                const recentDateText = doc.querySelector(".date")?.textContent || "";

                return (
                  <li key={post?.id || recentSlug} className="recent-post-item">
                    <Link to={`${blogBasePath}/${recentSlug}`}>
                      <img
                        src={imgSrc || defaultImg2}
                        alt={decodeHTML(title)}
                        className="recent-post-image"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = defaultImg2;
                        }}
                      />
                      <div className="recent-post-info">
                        <span className="recent-post-date">{recentDateText}</span>
                        <p className="recent-post-title title-p">{decodeHTML(title)}</p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      </div>
    </>
  );
};

export default withTranslation()(DetailBlog);
